import { Cable, PlacedEquipment, Rack, SimpleLayout } from '../types';
import { makeTemplateForPorts } from './layoutTemplates';
import { PORT } from './portLayout';
import { getEquipmentPortCapabilities } from '../data/equipmentPorts';

export const ICON_SIZE = 40;
export const ICON_HALF = 20;
export const PANEL_WIDTH = 180;
export const FIXED_ICON_HEIGHT = ICON_SIZE + 4;
/** One nameplate row (device name or custom label). */
export const NAMEPLATE_ROW_PX = 20;
/** Air between the nameplate bottom and the port panel. */
export const TOP_BADGE_NAMEPLATE_GAP_PX = 8;
/** Compact name-badge fallback width when DOM size is unknown. */
export const NAME_BADGE_FALLBACK_WIDTH = 120;
/** Lucide glyph beside the equipment name. */
export const NAME_BADGE_GLYPH_PX = 12;

export type BadgeAnchor =
  | 'top-left'
  | 'top-right'
  | 'center-top'
  | 'left'
  | 'right'
  | 'bottom-left'
  | 'bottom-right'
  | 'center-bottom';

export function isTopBadgeAnchor(anchor: BadgeAnchor): boolean {
  return anchor === 'top-left' || anchor === 'top-right' || anchor === 'center-top';
}

/** Y offset of the port panel when the name badge sits above it. */
export function panelOffsetYForTopBadge(_legacyIconSize: number, hasCustomLabel = false): number {
  const nameplateH = hasCustomLabel ? NAMEPLATE_ROW_PX * 2 : NAMEPLATE_ROW_PX;
  return nameplateH + TOP_BADGE_NAMEPLATE_GAP_PX;
}

export type Point = { x: number; y: number };

export interface CableEndpointRef {
  equipment: { id: string; name: string; type?: string };
  position: Point;
  placedId: string;
}

export interface CableGeometryContext {
  equipment: PlacedEquipment[];
  racks: Rack[];
  portsDisplayMode: 'none' | 'full';
  badgeAnchor: BadgeAnchor;
  canvasZoom: number;
  enableAdvancedCableRun?: boolean;
  draggedPositions?: Record<string, Point>;
  skipDomPortReads?: boolean;
  iconSize?: number;
}

export function extractPortNumber(portId: string): number | null {
  const parts = portId.split(':');
  if (parts.length >= 3 && parts[1] === 'port') {
    const portNum = parseInt(parts[2], 10);
    return Number.isNaN(portNum) ? null : portNum;
  }
  return null;
}

export function placedIdFromEndpoint(endpointId: string): string {
  const fullId = typeof endpointId === 'string' ? endpointId : String(endpointId);
  return fullId.includes(':port:') ? fullId.split(':port:')[0] : fullId;
}

export function isPortPanelVisible(
  placedId: string,
  equipment: PlacedEquipment[],
  portsDisplayMode: 'none' | 'full'
): boolean {
  const placedEq = equipment.find((eq) => eq.id === placedId);
  if (!placedEq) return false;
  const override = placedEq.config?.showPortPanel;
  return override !== undefined ? Boolean(override) : portsDisplayMode === 'full';
}

function liveOrStored(placedId: string, stored: Point, dragged?: Record<string, Point>): Point {
  return dragged?.[placedId] || stored;
}

export function findEquipmentById(
  id: string,
  ctx: CableGeometryContext
): CableEndpointRef | null {
  const { equipment, racks, draggedPositions } = ctx;
  const coreId = placedIdFromEndpoint(id);

  const standalone = equipment.find((eq) => eq.id === coreId)
    || equipment.find((eq) => coreId.startsWith(`${eq.id}:`));
  if (standalone) {
    return {
      equipment: standalone.equipment,
      position: liveOrStored(standalone.id, standalone.position, draggedPositions),
      placedId: standalone.id,
    };
  }

  const parts = coreId.split(':');
  if (parts.length === 3) {
    const [rackId, equipmentId] = parts;
    const rack = racks.find((r) => r.id === rackId);
    if (rack) {
      return {
        equipment: { id: equipmentId, name: equipmentId, type: 'rack-equipment' },
        position: { x: rack.position.x, y: rack.position.y },
        placedId: rack.id,
      };
    }
  }

  const underlyingMatches = equipment.filter((eq) => eq.equipment.id === coreId);
  if (underlyingMatches.length > 0) {
    const draggedMatch = underlyingMatches.find((eq) => Boolean(draggedPositions?.[eq.id]));
    const chosen = draggedMatch || underlyingMatches[0];
    return {
      equipment: chosen.equipment,
      position: liveOrStored(chosen.id, chosen.position, draggedPositions),
      placedId: chosen.id,
    };
  }

  for (const rack of racks) {
    if (rack.id === coreId) {
      return {
        equipment: { id: rack.id, name: rack.name, type: 'rack' },
        position: { x: rack.position.x, y: rack.position.y },
        placedId: rack.id,
      };
    }

    const rackEquipment = rack.equipment.find((re) => re.equipment.id === coreId);
    if (rackEquipment) {
      return {
        equipment: rackEquipment.equipment,
        position: { x: rack.position.x, y: rack.position.y },
        placedId: rack.id,
      };
    }
  }

  return null;
}

function getFrontFaceLayout(placedEq: PlacedEquipment): SimpleLayout {
  let frontFaceLayout: SimpleLayout | null = null;
  if (placedEq.config?.layout) {
    const multi = placedEq.config.layout.multiFace;
    const defaultFace = multi?.defaultFace || 'front';
    if (multi?.faces?.[defaultFace]) {
      frontFaceLayout = multi.faces[defaultFace] as SimpleLayout;
    } else if (multi?.faces?.front) {
      frontFaceLayout = multi.faces.front as SimpleLayout;
    } else if (placedEq.config.layout.simple) {
      frontFaceLayout = placedEq.config.layout.simple as SimpleLayout;
    }
  }
  if (!frontFaceLayout) {
    const portCount = placedEq.equipment.specifications?.ports || 8;
    frontFaceLayout = makeTemplateForPorts(portCount);
  }
  return frontFaceLayout;
}

/** Ports to use when attaching cables: custom layout first, then spec/database. */
export function getEquipmentCablePortCount(eq: PlacedEquipment): number {
  const layout = eq.config?.layout;
  const multi = layout?.multiFace;
  const defaultFace = multi?.defaultFace || 'front';
  const layoutPorts =
    multi?.faces?.[defaultFace]?.ports ||
    multi?.faces?.front?.ports ||
    layout?.simple?.ports;
  if (Array.isArray(layoutPorts) && layoutPorts.length > 0) return layoutPorts.length;
  const spec = eq.equipment.specifications?.ports;
  if (typeof spec === 'number' && spec > 0) return spec;
  const dbPorts = getEquipmentPortCapabilities(eq.equipment.id)?.length || 0;
  if (dbPorts > 0) return dbPorts;
  return 1;
}

function ctxIconSize(ctx: CableGeometryContext): number {
  return ctx.iconSize && ctx.iconSize > 0 ? ctx.iconSize : ICON_SIZE;
}

function panelOffsetForAnchor(
  anchor: BadgeAnchor,
  portCount: number,
  _iconSize = ICON_SIZE,
  hasCustomLabel = false,
  badgeWidth = NAME_BADGE_FALLBACK_WIDTH
): Point {
  const topPanelY = panelOffsetYForTopBadge(0, hasCustomLabel);
  switch (anchor) {
    case 'top-right':
      return { x: -PANEL_WIDTH + badgeWidth, y: topPanelY };
    case 'center-top':
      return { x: -PANEL_WIDTH / 2 + badgeWidth / 2, y: topPanelY };
    case 'left':
      return { x: badgeWidth + 4, y: 0 };
    case 'right':
      return { x: -PANEL_WIDTH - 4, y: 0 };
    case 'bottom-left': {
      const estimatedRows = Math.ceil(portCount / 8);
      return { x: 0, y: -(estimatedRows * 55 + 20) - 4 };
    }
    case 'bottom-right': {
      const estimatedRows = Math.ceil(portCount / 8);
      return { x: -PANEL_WIDTH + badgeWidth, y: -(estimatedRows * 55 + 20) - 4 };
    }
    case 'center-bottom': {
      const estimatedRows = Math.ceil(portCount / 8);
      return { x: -PANEL_WIDTH / 2 + badgeWidth / 2, y: -(estimatedRows * 55 + 20) - 4 };
    }
    case 'top-left':
    default:
      return { x: 0, y: topPanelY };
  }
}

export function readNameplateCenterFromDom(
  placedId: string,
  canvasZoom: number
): Point | null {
  try {
    const el = document.querySelector(
      `[data-equipment-id="${placedId}"] [data-equipment-nameplate]`
    ) as HTMLElement | null;
    const layerEl = document.querySelector('[data-panning-layer="true"]') as HTMLElement | null;
    if (!el || !layerEl) return null;
    const rect = el.getBoundingClientRect();
    const layerRect = layerEl.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    const zoom = canvasZoom > 0 ? canvasZoom : 1;
    return {
      x: (rect.left + rect.width / 2 - layerRect.left) / zoom,
      y: (rect.top + rect.height / 2 - layerRect.top) / zoom,
    };
  } catch {
    return null;
  }
}

export function readPortCenterFromDom(
  placedId: string,
  portNum: number,
  canvasZoom: number,
  kind?: string
): Point | null {
  try {
    const stageEl = document.querySelector(
      `[data-port-stage-for="${placedId}"]`
    ) as HTMLElement | null;
    const layerEl = document.querySelector('[data-panning-layer="true"]') as HTMLElement | null;
    if (!stageEl || !layerEl) return null;
    const ports = Array.from(stageEl.querySelectorAll<HTMLElement>('.port'));
    const kindNorm = kind != null && kind !== '' ? String(kind) : undefined;
    let portEl =
      ports.find((el) => {
        const num = el.getAttribute('data-port-number');
        if (num !== String(portNum)) return false;
        if (!kindNorm) return true;
        return (el.getAttribute('data-port-kind') || '') === kindNorm;
      }) || null;
    if (!portEl && !kindNorm) {
      portEl =
        ports.find((el) => {
          const label = el.querySelector('.number')?.textContent?.trim();
          return label === String(portNum);
        }) || null;
    }
    if (!portEl) return null;
    const portRect = portEl.getBoundingClientRect();
    const layerRect = layerEl.getBoundingClientRect();
    if (portRect.width <= 0 || portRect.height <= 0) return null;
    const zoom = canvasZoom > 0 ? canvasZoom : 1;
    return {
      x: (portRect.left + portRect.width / 2 - layerRect.left) / zoom,
      y: (portRect.top + portRect.height / 2 - layerRect.top) / zoom,
    };
  } catch {
    return null;
  }
}

export function getPortPosition(
  portId: string,
  equipmentData: CableEndpointRef | null,
  ctx: CableGeometryContext
): Point | null {
  if (!equipmentData) return null;

  const portNum = extractPortNumber(portId);
  if (portNum === null) return null;

  const kindMatch = portId.match(/:kind:([^:]+)/);
  const kind = kindMatch ? decodeURIComponent(kindMatch[1]) : undefined;

  const fromDom = readPortCenterFromDom(equipmentData.placedId, portNum, ctx.canvasZoom, kind);
  if (fromDom) return fromDom;

  const placedEq = ctx.equipment.find((eq) => eq.id === equipmentData.placedId);
  if (!placedEq) return null;

  const frontFaceLayout = getFrontFaceLayout(placedEq);
  const port = kind
    ? frontFaceLayout.ports.find((p) => p.number === portNum && String(p.kind) === kind)
    : frontFaceLayout.ports.find((p) => p.number === portNum);
  if (!port) return null;

  const eqPos = liveOrStored(equipmentData.placedId, equipmentData.position, ctx.draggedPositions);
  const equipmentBadgeAnchor = (placedEq.config?.badgeAnchor || ctx.badgeAnchor || 'top-left') as BadgeAnchor;
  const hasCustomLabel = typeof placedEq.config?.label === 'string' && placedEq.config.label.trim().length > 0;
  const offset = panelOffsetForAnchor(
    equipmentBadgeAnchor,
    frontFaceLayout.ports.length,
    ctxIconSize(ctx),
    hasCustomLabel
  );

  return {
    x: eqPos.x + offset.x + port.x + PORT.width / 2 + 1,
    y: eqPos.y + offset.y + port.y + PORT.height / 2 + 1,
  };
}

export function getCableEndpoint(
  data: CableEndpointRef,
  ctx: CableGeometryContext,
  portId?: string
): Point {
  const panelVisible = isPortPanelVisible(data.placedId, ctx.equipment, ctx.portsDisplayMode);
  const portNum = portId ? extractPortNumber(portId) : null;
  const hasSpecificPort = portNum !== null && !!portId && portId.includes(':port:');

  if (panelVisible && hasSpecificPort && portId) {
    const portPos = getPortPosition(portId, data, ctx);
    if (portPos) return portPos;
    // Specific port missing after a layout change — fall through to badge, never another port.
  } else if (panelVisible && ctx.enableAdvancedCableRun && ctx.portsDisplayMode === 'full' && !hasSpecificPort) {
    const defaultPortPos = getPortPosition(`${data.placedId}:port:1`, data, ctx);
    if (defaultPortPos) return defaultPortPos;
  }

  const fromNameplate = readNameplateCenterFromDom(data.placedId, ctx.canvasZoom);
  if (fromNameplate) return fromNameplate;

  const pos = liveOrStored(data.placedId, data.position, ctx.draggedPositions);
  const halfW = NAME_BADGE_FALLBACK_WIDTH / 2;
  const halfH = NAMEPLATE_ROW_PX / 2;
  if (!pos || typeof pos.x !== 'number' || typeof pos.y !== 'number') {
    return { x: halfW, y: halfH };
  }
  return { x: pos.x + halfW, y: pos.y + halfH };
}

function getUnderlyingBaseId(id: string) {
  return typeof id === 'string' ? id.split(':')[0] : id;
}

function refineEndpointIfAmbiguous(
  endpointId: string,
  otherPosition: Point,
  ctx: CableGeometryContext
): CableEndpointRef | null {
  const baseId = getUnderlyingBaseId(endpointId);
  const placedMatch = ctx.equipment.find((eq) => eq.id === baseId);
  if (placedMatch) return findEquipmentById(endpointId, ctx);

  const candidates = ctx.equipment.filter((eq) => eq.equipment.id === baseId);
  if (candidates.length <= 1) return findEquipmentById(endpointId, ctx);

  let best = candidates[0];
  let bestDist = Infinity;
  for (const c of candidates) {
    const pos = liveOrStored(c.id, c.position, ctx.draggedPositions);
    const fromNameplate = readNameplateCenterFromDom(c.id, ctx.canvasZoom);
    const center = fromNameplate || {
      x: pos.x + NAME_BADGE_FALLBACK_WIDTH / 2,
      y: pos.y + NAMEPLATE_ROW_PX / 2,
    };
    const dx = center.x - otherPosition.x;
    const dy = center.y - otherPosition.y;
    const d2 = dx * dx + dy * dy;
    if (d2 < bestDist) {
      bestDist = d2;
      best = c;
    }
  }
  return {
    equipment: best.equipment,
    position: liveOrStored(best.id, best.position, ctx.draggedPositions),
    placedId: best.id,
  };
}

export interface ResolvedCableLine {
  source: Point;
  dest: Point;
  sourceData: CableEndpointRef;
  destData: CableEndpointRef;
}

export function resolveCableEndpoints(
  cable: Cable,
  ctx: CableGeometryContext
): ResolvedCableLine | null {
  let sourceData = findEquipmentById(cable.from, ctx);
  let destData = findEquipmentById(cable.to, ctx);
  if (!sourceData || !destData) return null;

  let source = getCableEndpoint(sourceData, ctx, cable.from);
  let dest = getCableEndpoint(destData, ctx, cable.to);

  const refinedSource = refineEndpointIfAmbiguous(cable.from, dest, ctx);
  if (refinedSource) {
    sourceData = refinedSource;
    source = getCableEndpoint(refinedSource, ctx, cable.from);
  }
  const refinedDest = refineEndpointIfAmbiguous(cable.to, source, ctx);
  if (refinedDest) {
    destData = refinedDest;
    dest = getCableEndpoint(refinedDest, ctx, cable.to);
  }

  return { source, dest, sourceData, destData };
}

export function getCableColor(type: string, isExisting?: boolean): string {
  if (isExisting) {
    switch (type) {
      case 'ethernet': return '#9ca3af';
      case 'fiber': return '#a78bfa';
      case 'power': return '#f87171';
      default: return '#6b7280';
    }
  }
  switch (type) {
    case 'ethernet': return '#3b82f6';
    case 'fiber': return '#f59e0b';
    case 'power': return '#ef4444';
    default: return '#6b7280';
  }
}

export function brightenColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.min(255, (num >> 16) + amt);
  const G = Math.min(255, ((num >> 8) & 0x00FF) + amt);
  const B = Math.min(255, (num & 0x0000FF) + amt);
  return '#' + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1);
}

export function isFlexiRouting(routing?: string | null): boolean {
  return routing === 'flexi';
}

/** Google Drawings-style cubic stubs along the dominant axis. */
export function cablePathMidpoint(p1: Point, p2: Point, flexi: boolean): Point {
  if (!flexi) {
    return { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
  }
  const { c1, c2 } = flexiControlPoints(p1, p2);
  return cubicBezierPoint(0.5, p1, c1, c2, p2);
}

/** Side of a node the cable should leave toward `toward`. */
export type CableAttachSide = 'top' | 'bottom' | 'left' | 'right';

export function cableAttachSide(from: Point, toward: Point): CableAttachSide {
  const dx = toward.x - from.x;
  const dy = toward.y - from.y;
  if (Math.abs(dx) >= Math.abs(dy)) return dx >= 0 ? 'right' : 'left';
  return dy >= 0 ? 'bottom' : 'top';
}

/** Offset from a port/badge center onto the edge facing `toward`. */
export function cableEndpointAttachment(
  center: Point,
  toward: Point,
  halfW: number,
  halfH: number
): Point {
  const side = cableAttachSide(center, toward);
  switch (side) {
    case 'right':
      return { x: center.x + halfW, y: center.y };
    case 'left':
      return { x: center.x - halfW, y: center.y };
    case 'bottom':
      return { x: center.x, y: center.y + halfH };
    case 'top':
      return { x: center.x, y: center.y - halfH };
  }
}

/** Google Drawings-style cubic stubs along the dominant axis. */
export function flexiControlPoints(p1: Point, p2: Point): { c1: Point; c2: Point } {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const dist = Math.hypot(dx, dy);
  const k = Math.max(24, Math.min(120, dist * 0.35));
  if (Math.abs(dx) >= Math.abs(dy)) {
    const sx = dx >= 0 ? 1 : -1;
    return {
      c1: { x: p1.x + sx * k, y: p1.y },
      c2: { x: p2.x - sx * k, y: p2.y },
    };
  }
  const sy = dy >= 0 ? 1 : -1;
  return {
    c1: { x: p1.x, y: p1.y + sy * k },
    c2: { x: p2.x, y: p2.y - sy * k },
  };
}

function cubicBezierPoint(t: number, p0: Point, p1: Point, p2: Point, p3: Point): Point {
  const u = 1 - t;
  const tt = t * t;
  const uu = u * u;
  return {
    x: uu * u * p0.x + 3 * uu * t * p1.x + 3 * u * tt * p2.x + tt * t * p3.x,
    y: uu * u * p0.y + 3 * uu * t * p1.y + 3 * u * tt * p2.y + tt * t * p3.y,
  };
}

function distPointToSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(px - ax, py - ay);
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

export function isPointNearBezier(
  p: Point,
  p0: Point,
  c1: Point,
  c2: Point,
  p3: Point,
  threshold = 8
): boolean {
  const minX = Math.min(p0.x, c1.x, c2.x, p3.x) - threshold;
  const maxX = Math.max(p0.x, c1.x, c2.x, p3.x) + threshold;
  const minY = Math.min(p0.y, c1.y, c2.y, p3.y) - threshold;
  const maxY = Math.max(p0.y, c1.y, c2.y, p3.y) + threshold;
  if (p.x < minX || p.x > maxX || p.y < minY || p.y > maxY) return false;
  // Equal-t samples cluster near the first endpoint; step by approximate length so
  // the far end and the belly of the S-curve stay hittable.
  const approxLen =
    Math.hypot(c1.x - p0.x, c1.y - p0.y) +
    Math.hypot(c2.x - c1.x, c2.y - c1.y) +
    Math.hypot(p3.x - c2.x, p3.y - c2.y);
  const steps = Math.max(24, Math.min(80, Math.ceil(approxLen / 10)));
  let prev = p0;
  for (let i = 1; i <= steps; i++) {
    const cur = cubicBezierPoint(i / steps, p0, c1, c2, p3);
    if (distPointToSegment(p.x, p.y, prev.x, prev.y, cur.x, cur.y) <= threshold) return true;
    prev = cur;
  }
  return false;
}
